import React from 'react'
import PropTypes from 'prop-types'

import useDataProcess from 'hooks/useDataProcess'
import useSnackbar from 'hooks/useSnackbar'
import useSelectCountry from 'hooks/useSelectCountry'

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  Typography,
  Button,
} from '@material-ui/core'

import NewForm from './components/NewForm'
import DataCollectedModalProvider from 'providers/DataCollectedModalProvider'

import helpers from 'helpers'
import * as service from 'service'

const DialogDataTreatment = ({
  dataProcessId,
  open,
  setLoading = () => {},
  setOpen = () => {},
  onEvent = () => {},
  ...props
}) => {
  const { convertFormDataToArray } = helpers.functions

  const { reloadOptions } = useDataProcess()

  const { selectedCountries, manySelectedCountries } = useSelectCountry()

  const snackbar = useSnackbar()
  const edition = props.dataTreatment && props.dataTreatment.id ? true : false

  const onSubmit = (dataTreatment, template) => {
    if (props.dataTreatment && props.dataTreatment.id) {
      handleEdit(dataTreatment, template)
    } else {
      handleCreate(dataTreatment, template)
    }
  }

  const handleCreate = async (dataTreatment, template) => {
    try {
      setLoading(true)
      setOpen(false)

      const normalizedDataTreatment = convertFormDataToArray(
        dataTreatment,
        'sharedSoftwares',
      )

      let response = await service.dponet.dataTreatments.create({
        dataProcessId: dataProcessId,
        dataTreatment: {
          ...normalizedDataTreatment,
          internationalTransferCountries: selectedCountries,
          dataProcessTemplateQuestions: template,
        },
      })

      const createdDataTreatmentId = response?.data?.dataTreatment?.id
      const createdDataProcessId = response?.data?.dataTreatment?.dataProcessId

      await submitSharedSoftwares(
        normalizedDataTreatment.sharedSoftwares,
        createdDataTreatmentId,
        createdDataProcessId,
      )

      await submitPendingSharedCollectedFields(
        dataTreatment.pendingSharedCollectedFields,
        createdDataTreatmentId,
        createdDataProcessId,
      )

      onEvent()
      reloadOptions()
      setLoading(false)
      snackbar.open({
        message: 'Compartilhamento de dados criado com sucesso!',
        variant: 'success',
      })
    } catch (error) {
      setOpen(false)
      setLoading(false)
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    }
  }

  const handleEdit = async (dataTreatment, template) => {
    try {
      setLoading(true)
      setOpen(false)

      let response = await service.dponet.dataTreatments.put({
        dataProcessId: dataProcessId,
        dataTreatmentId: dataTreatment.dataTreatmentId,
        dataTreatment: {
          ...dataTreatment,
          internationalTransferCountries: selectedCountries,
          dataProcessTemplateQuestions: template,
        },
      })

      await submitSharedSoftwares(
        dataTreatment.sharedSoftwares,
        response?.data?.dataTreatment?.id,
        response?.data?.dataTreatment?.dataProcessId,
      )

      onEvent()
      reloadOptions()
      setLoading(false)
      snackbar.open({
        message: 'Compartilhamento de dados atualizado com sucesso!',
        variant: 'success',
      })
    } catch (error) {
      setOpen(false)
      setLoading(false)
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    }
  }

  const submitSharedSoftwares = async (
    sharedSoftwares,
    dataTreatmentId,
    dataProcessId,
  ) => {
    if (!sharedSoftwares?.length) return

    for (let index in sharedSoftwares) {
      let sharedSoftware = sharedSoftwares[index]
      if (!!sharedSoftware.id) {
        await service.dponet.sharedSoftwares.put({
          dataProcessId,
          dataTreatmentId,
          sharedSoftwareId: sharedSoftware.id,
          sharedSoftware: {
            ...sharedSoftware,
            internationalTransferCountries:
              manySelectedCountries[`sharedSoftwares-${index}`],
          },
        })
      } else {
        await service.dponet.sharedSoftwares.create({
          dataProcessId,
          dataTreatmentId,
          sharedSoftware: {
            ...sharedSoftware,
            internationalTransferCountries:
              manySelectedCountries[`sharedSoftwares-${index}`],
          },
        })
      }
    }
  }

  const submitPendingSharedCollectedFields = async (
    pendingSharedCollectedFields,
    dataTreatmentId,
    dataProcessId,
  ) => {
    if (!pendingSharedCollectedFields?.length) return

    for (const pendingField of pendingSharedCollectedFields) {
      const sharedCollectedFieldsIds = pendingField.collectedFields?.map(
        (field) => field.id,
      )

      if (sharedCollectedFieldsIds?.length) {
        await service.dponet.sharedCollectedFields.create({
          dataProcessId,
          dataTreatmentId,
          sharedCollectedField: {
            sharedCollectedFieldsIds,
          },
        })
      }
    }
  }

  return (
    <DataCollectedModalProvider dataCollecteds={[]}>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return

          setOpen(false)
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h4">
              {edition ? 'Editar' : 'Adicionar'} compartilhamento de dados
            </Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <NewForm
            onSubmit={onSubmit}
            onEvent={onEvent}
            dataTreatment={props.dataTreatment}
            dataProcessId={dataProcessId}
          />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Box pl={2} pr={1}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => setOpen(false)}
            >
              Voltar
            </Button>
          </Box>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            form="data-treatment-form"
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </DataCollectedModalProvider>
  )
}

DialogDataTreatment.propTypes = {
  dataProcessId: PropTypes.number.isRequired,
  open: PropTypes.bool,
  setOpen: PropTypes.func.isRequired,
  setLoading: PropTypes.func,
  onEvent: PropTypes.func,
}

export default DialogDataTreatment
